import { Link } from "react-router-dom";
import { MapPin, Eye, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { StarRating } from "@/components/StarRating";
import { resolveUrl } from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";
import { useLang } from "@/context/LanguageContext";
import { SUBCATEGORIES, SPORTS } from "@/constants/categories";

export const ActivityCard = ({ business, index = 0, audience }) => {
  const img = business.cover_image || business.photos?.[0];
  const { isFavorite, toggleFavorite } = useFavorites();
  const { t, tt } = useLang();
  const tagObj = SPORTS[business.sport] || SUBCATEGORIES[business.subcategory];
  const tag = tagObj ? tt(tagObj) : (typeof business.category === "string" ? business.category : "");
  const fav = isFavorite(business.id);

  const onFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleFavorite(business.id);
    toast.success(added ? t("fav.saved") : t("fav.removed"));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.4) }}
    >
      <Link
        to={`/activity/${business.id}`}
        state={audience ? { audience } : undefined}
        data-testid="activity-card-item"
        className="group block bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
      >
        <div className="relative h-44 bg-muted">
          {img ? (
            <img src={resolveUrl(img)} alt={business.name} loading="eager" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full grid place-items-center text-muted-foreground"><MapPin /></div>
          )}
          {tag && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-bold" data-testid="activity-card-tag">
              {tag}
            </span>
          )}
          <button
            type="button"
            onClick={onFav}
            aria-label="favorite"
            data-testid="activity-card-favorite-btn"
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/90 hover:bg-white shadow-sm transition-colors"
          >
            <Heart size={17} className={fav ? "fill-rose-500 text-rose-500" : "text-slate-500"} />
          </button>
          {business.rating > 0 && (
            <span className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 text-slate-900 text-xs font-bold" data-testid="activity-card-rating">
              <StarRating value={business.rating} size={12} /> {business.rating.toFixed(1)}
            </span>
          )}
          {/* Round business logo overlapping image and card body */}
          {business.logo && (
            <img
              src={resolveUrl(business.logo)}
              alt=""
              data-testid="activity-card-logo"
              className="absolute -bottom-7 left-5 h-16 w-16 rounded-full object-cover ring-4 ring-white dark:ring-slate-800 bg-white shadow-md z-10"
            />
          )}
        </div>
        <div className={`p-5 ${business.logo ? "pt-9" : ""}`}>
          <h3 className="font-display font-bold text-lg leading-snug line-clamp-1" data-testid="activity-card-title">{business.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{business.description}</p>
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin size={14} /> {business.city || "—"}</span>
            <span className="flex items-center gap-1"><Eye size={14} /> {business.views || 0}</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
